import { formatFileSize, formatDate } from '@/mocks/driveFiles';
import type { DriveItem } from '@/mocks/driveFiles';
import FileThumbnail from './FileThumbnail';

interface FileGridProps {
  files: DriveItem[];
  parentId: string | null;
  selectedItemId: string | null;
  selectedIds: Set<string>;
  dragOverFolderId: string | null;
  isTrashView?: boolean;
  onFileClick: (file: DriveItem, e?: React.MouseEvent) => void;
  onFolderClick: (folderId: string) => void;
  onContextMenu: (e: React.MouseEvent, item: DriveItem) => void;
  onDragStart: (e: React.DragEvent, item: DriveItem) => void;
  onDragEnd: () => void;
  onDragOver: (e: React.DragEvent, folderId: string) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent, folderId: string) => void;
  onToggleSelect: (itemId: string, e: React.MouseEvent) => void;
}

function getDaysLeft(deletedAt?: string): string {
  if (!deletedAt) return '';
  const TRASH_RETENTION_DAYS = 30;
  const now = new Date();
  const deleted = new Date(deletedAt);
  const diffMs = now.getTime() - deleted.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const remaining = TRASH_RETENTION_DAYS - diffDays;
  if (remaining <= 0) return '곧 삭제됨';
  if (remaining === 1) return '내일 삭제됨';
  return `${remaining}일 남음`;
}

export default function FileGrid({
  files,
  parentId,
  selectedItemId,
  selectedIds,
  dragOverFolderId,
  isTrashView = false,
  onFileClick,
  onFolderClick,
  onContextMenu,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDragLeave,
  onDrop,
  onToggleSelect,
}: FileGridProps) {
  const items = isTrashView ? files : files.filter((f) => f.parentId === parentId);
  const folders = items.filter((f) => f.type === 'folder');
  const fileItems = items.filter((f) => f.type === 'file');
  const sorted = [...folders, ...fileItems];

  if (sorted.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-foreground-500 animate-in fade-in duration-300">
        <span className="w-20 h-20 flex items-center justify-center mb-4">
          <i className={`text-5xl ${isTrashView ? 'ri-delete-bin-line text-foreground-300' : 'ri-folder-open-line text-secondary-300'}`}></i>
        </span>
        <p className="text-lg font-medium text-foreground-600">
          {isTrashView ? '휴지통이 비어 있습니다' : '이 폴더는 비어 있습니다'}
        </p>
        <p className="text-sm text-foreground-500 mt-1">
          {isTrashView ? '삭제된 파일이 여기에 30일간 보관됩니다' : '파일을 업로드하거나 새 폴더를 만들어보세요'}
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 md:gap-4">
      {sorted.map((item, index) => {
        const isSelected = selectedIds.has(item.id);
        const isContextActive = selectedItemId === item.id;
        const isDragOver = item.type === 'folder' && dragOverFolderId === item.id && !isTrashView;

        const borderClass = isSelected
          ? 'border-primary-400 bg-primary-50/70 ring-2 ring-primary-200'
          : isContextActive
            ? 'border-primary-300 bg-primary-50/50 ring-2 ring-primary-100'
            : isDragOver
              ? 'border-accent-400 bg-accent-50/80 ring-2 ring-accent-300 scale-[1.03]'
              : isTrashView
                ? 'border-transparent hover:border-accent-200 hover:bg-accent-50/40'
                : 'border-transparent hover:border-background-300 hover:bg-background-100';

        return (
          <div
            key={item.id}
            className="relative animate-in fade-in slide-in-from-bottom-3 duration-200"
            style={{ animationDelay: `${Math.min(index * 30, 300)}ms`, animationFillMode: 'backwards' }}
          >
            <button
              draggable={!isTrashView}
              onDragStart={(e) => onDragStart(e, item)}
              onDragEnd={onDragEnd}
              onClick={(e) => {
                if (item.type === 'folder') {
                  onFolderClick(item.id);
                } else {
                  onFileClick(item, e);
                }
              }}
              onContextMenu={(e) => onContextMenu(e, item)}
              {...(item.type === 'folder' && !isTrashView
                ? {
                    onDragOver: (e: React.DragEvent) => onDragOver(e, item.id),
                    onDragLeave,
                    onDrop: (e: React.DragEvent) => onDrop(e, item.id),
                  }
                : {})}
              className={`group flex flex-col items-center p-3 md:p-4 rounded-lg border transition-all duration-150 cursor-pointer text-center w-full ${borderClass}`}
            >
              <span className={`w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-xl mb-2.5 relative transition-transform duration-200 ${
                isDragOver ? 'scale-110' : ''
              }`}>
                {item.type === 'folder' ? (
                  <>
                    <span className="w-full h-full flex items-center justify-center rounded-xl bg-accent-50">
                      <i className={`ri-folder-3-fill text-3xl md:text-4xl ${isTrashView ? 'text-accent-400' : 'text-accent-500'}`}></i>
                    </span>
                    {isDragOver && (
                      <span className="absolute inset-0 flex items-center justify-center rounded-xl bg-accent-500/20">
                        <i className="ri-folder-transfer-line text-2xl text-accent-600"></i>
                      </span>
                    )}
                  </>
                ) : (
                  <FileThumbnail item={item} size="md" />
                )}
                {isSelected && !isDragOver && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center rounded-full bg-primary-500">
                    <i className="ri-check-line text-white text-xs"></i>
                  </span>
                )}
              </span>
              <span className={`text-sm font-medium truncate w-full max-w-[120px] ${isTrashView ? 'text-foreground-600' : 'text-foreground-800'}`}>
                {item.name}
              </span>
              <span className="text-xs text-foreground-500 mt-0.5">
                {item.type === 'folder' ? '폴더' : formatFileSize(item.size ?? 0)}
              </span>
              {isTrashView && item.deletedAt ? (
                <span className="text-xs text-accent-500 mt-0.5 font-medium">
                  {getDaysLeft(item.deletedAt)}
                </span>
              ) : (
                <span className="text-xs text-foreground-400 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  {formatDate(item.updatedAt)}
                </span>
              )}
            </button>

            <button
              onClick={(e) => onToggleSelect(item.id, e)}
              className={`absolute top-2 right-2 w-6 h-6 flex items-center justify-center rounded-md border transition-all duration-150 cursor-pointer ${
                isSelected
                  ? 'bg-primary-500 border-primary-500 text-white opacity-100'
                  : 'border-background-300 bg-background-50 text-transparent hover:border-foreground-400 hover:text-foreground-400 opacity-0 group-hover:opacity-100'
              }`}
              title={isSelected ? '선택 해제' : '선택'}
            >
              <i className={`${isSelected ? 'ri-check-line' : 'ri-checkbox-blank-circle-line'} text-sm`}></i>
            </button>
          </div>
        );
      })}
    </div>
  );
}